import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { getAuth, updateProfile } from "firebase/auth";
import { useAuth } from "../../firebase/authContext";
import { FiCamera } from "react-icons/fi";

const ProfilePage = () => {
  const { userId } = useParams();
  const { currentUser } = useAuth();
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [profileData, setProfileData] = useState(null);
  const [bio, setBio] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [videoSet, setVideoSet] = useState("all");
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [videoErrors, setVideoErrors] = useState({});

  // Check if the profile belongs to the logged-in user
  const isCurrentUser = currentUser?.uid === userId;

  useEffect(() => {
    if (!userId) return;

    const fetchProfile = async () => {
      try {
        // If we have state from navigation, use that data first
        if (location.state?.profileData) {
          const navProfileData = location.state.profileData;
          setProfileData(navProfileData);
          setBio(navProfileData.bio || "Dancer & Creator");
          setImageUrl(
            navProfileData.profileImage ||
              "https://ui-avatars.com/api/?name=User&background=random"
          );
        }

        // Fetch profile details from Firestore
        const docRef = doc(db, "users", userId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const userData = { ...docSnap.data(), uid: userId };
          // Only update if we don't have navigation state or if this is the current user's profile
          if (!location.state?.profileData || isCurrentUser) {
            setProfileData(userData);
            setBio(userData.bio || "Dancer & Creator");
            setImageUrl(
              userData.profileImage ||
                "https://ui-avatars.com/api/?name=User&background=random"
            );
          }
        } else {
          console.log("No user found, creating new user document");
          // Create a new user document if it doesn't exist
          const newUserData = {
            username: location.state?.profileData?.username || "New User",
            bio: "Dancer & Creator",
            profileImage:
              "https://ui-avatars.com/api/?name=User&background=random",
            createdAt: new Date().toISOString(),
            uid: userId,
          };

          await setDoc(docRef, newUserData);
          setProfileData(newUserData);
          setBio(newUserData.bio);
          setImageUrl(newUserData.profileImage);
        }

        // Fetch uploaded videos from your backend
        const videosResponse = await fetch(
          `https://dance-ai-app.azurewebsites.net/api/user_videos/${userId}`
        );
        const videosData = await videosResponse.json();

        console.log("Raw videos data:", videosData);

        let videos = [];
        if (videosResponse.ok) {
          // Handle the nested structure
          if (videosData.videos && Array.isArray(videosData.videos)) {
            // Extract the actual video objects from the nested structure
            videos = videosData.videos
              .flatMap((item) => {
                if (item.videos && Array.isArray(item.videos)) {
                  return item.videos;
                }
                return item;
              })
              .filter((video) => video && video.video_url); // Filter out any invalid entries
          }

          console.log("Extracted videos:", videos);

          const organizedVideos = videos.map((video) => ({
            ...video,
            video_id: video.video_id || `video-${Math.random()}`,
            metadata: {
              title: video.title || "Untitled",
              duration: video.duration || "N/A",
              style: video.style || "Unknown",
              uploadedAt:
                video.upload_date ||
                video.uploaded_at ||
                new Date().toISOString(),
              views: video.views || 0,
              likes: video.likes || 0,
            },
          }));

          console.log("Organized videos:", organizedVideos);
          setUploadedVideos(organizedVideos);
        } else {
          console.error("Error fetching videos:", videosData);
          setUploadedVideos([]);
        }
      } catch (error) {
        console.error("Error fetching profile or videos:", error);
        setUploadedVideos([]);
      }
    };

    fetchProfile();
  }, [userId, currentUser, location.state, isCurrentUser]);

  const handleSave = async () => {
    if (isCurrentUser) {
      const userRef = doc(db, "users", currentUser.uid);
      await setDoc(userRef, { bio }, { merge: true });
      setEditMode(false);
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const storage = getStorage();
    const storageRef = ref(storage, `profilePictures/${userId}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      null,
      (error) => console.error("Upload failed:", error),
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then(async (downloadURL) => {
          setImageUrl(downloadURL);

          if (isCurrentUser) {
            await updateProfile(auth.currentUser, { photoURL: downloadURL });

            const userRef = doc(db, "users", currentUser.uid);
            await setDoc(
              userRef,
              { profileImage: downloadURL },
              { merge: true }
            );
          }
        });
      }
    );
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log("Uploading file:", file.name);
      const formData = new FormData();
      formData.append("video", file);

      formData.append("uploaded_by", currentUser.uid); // or coachingCenterID
      formData.append("uploaded_by_type", "user"); // or "coaching_center"
      formData.append("title", file.name);
      formData.append("description", "Your optional description here");

      fetch("https://dance-ai-app.azurewebsites.net/upload", {
        method: "POST",
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to upload video");
          }
          console.log("Video uploaded successfully!");
          return response.json();
        })
        .then((data) => {
          fetch(`https://dance-ai-app.azurewebsites.net/get_frame_info`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ videoFilename: file.name }),
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error("Failed to get frame info");
              }
              return response.json();
            })
            .then((data) => {
              console.log("Frame Rate:", data.frameRate);
            })
            .catch((error) =>
              console.error("Error getting frame rate:", error)
            );
        })
        .catch((error) => console.error("Error uploading video:", error));
    }
  };

  const myVideosHandler = () => setVideoSet("myVideos");
  const recentVideosHandler = () => setVideoSet("recentVideos");

  const handleVideoError = (videoId) => {
    setVideoErrors((prev) => ({
      ...prev,
      [videoId]: true,
    }));
  };

  return (
    <div className="flex flex-col h-full bg-gray-100 p-6">
      {/* Profile Section */}
      <div className="flex flex-col sm:flex-row items-center p-6 border-b border-gray-300">
        <div className="relative mb-4 sm:mb-0 sm:mr-6">
          <img
            src={imageUrl}
            alt="User Profile"
            className="w-32 h-32 rounded-full object-cover"
            onError={(e) => {
              e.target.src =
                "https://ui-avatars.com/api/?name=User&background=random";
            }}
          />
          {isCurrentUser && (
            <div className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-50 rounded-full opacity-0 hover:opacity-100 transition duration-200 ease-in">
              <label className="cursor-pointer">
                <FiCamera className="text-white text-2xl" />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
              </label>
            </div>
          )}
        </div>

        <div className="text-center sm:text-left">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {profileData?.username}
          </h2>

          {isCurrentUser ? (
            editMode ? (
              <>
                <textarea
                  className="mt-2 border rounded-md p-2 w-full text-gray-600"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Enter your bio"
                />
                <button
                  onClick={handleSave}
                  className="mt-2 px-4 py-2 bg-blue-500 text-white rounded"
                >
                  Save
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-600">{bio}</p>
                <button
                  onClick={() => setEditMode(true)}
                  className="mt-2 px-4 py-2 bg-indigo-500 text-white rounded"
                >
                  Edit Profile
                </button>
              </>
            )
          ) : (
            <p className="text-gray-600">{bio}</p>
          )}
        </div>
      </div>

      {/* Buttons Section */}
      {isCurrentUser && (
        <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4 py-4 mx-10">
          <button
            onClick={myVideosHandler}
            className="w-full sm:w-auto bg-blue-500 text-white py-2 px-6 rounded-lg hover:bg-blue-600"
          >
            My Videos
          </button>
          <button
            onClick={recentVideosHandler}
            className="w-full sm:w-auto bg-blue-500 text-white py-2 px-6 rounded-lg hover:bg-blue-600"
          >
            Recent Activity
          </button>
        </div>
      )}

      {/* Video Section */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
        {uploadedVideos.length > 0 ? (
          uploadedVideos.map((video) => {
            const videoKey = video.video_id || `video-${Math.random()}`;
            return (
              <div
                key={videoKey}
                className="relative bg-gray-200 rounded-lg shadow-md overflow-hidden group"
              >
                {video.video_url && !videoErrors[videoKey] ? (
                  <video
                    src={video.video_url}
                    className="w-full h-[150px] object-cover transition-transform duration-200 ease-in-out group-hover:scale-105"
                    onMouseOver={(e) => {
                      if (e.target.readyState >= 2) {
                        e.target.play();
                      }
                    }}
                    onMouseOut={(e) => {
                      e.target.pause();
                      e.target.currentTime = 0;
                    }}
                    onError={(e) => {
                      console.error("Video loading error:", e);
                      handleVideoError(videoKey);
                    }}
                    muted
                    loop
                    preload="metadata"
                  />
                ) : (
                  <div className="w-full h-[150px] bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-500">Video unavailable</span>
                  </div>
                )}
                <div className="p-2">
                  <p className="font-semibold">{video.metadata.title}</p>
                  <p className="text-sm text-gray-600">
                    {video.metadata.duration} | {video.metadata.style}
                  </p>
                  <p className="text-xs text-gray-500">
                    Uploaded:{" "}
                    {new Date(video.metadata.uploadedAt).toLocaleDateString()}
                  </p>
                </div>
                {/* Learn Button */}
                {video.video_url && !videoErrors[videoKey] && (
                  <button
                    className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
                    onClick={() =>
                      navigate("/review", {
                        state: {
                          videoUrl: video.video_url,
                          videoFilename: video.video_url.split("/").pop(),
                          userId: profileData.uid,
                          username: profileData.username,
                          videoMetadata: video.metadata,
                        },
                      })
                    }
                  >
                    Learn
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <p className="text-gray-500">No videos uploaded yet.</p>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
